import type { Feature, FeatureCollection, Polygon } from "geojson";

import { IGeoJSON, ILocationInfo } from "@ryde/shared";

import {
  MAPLIBRE_POLYGON_STROKE_OPACITY,
  MAPLIBRE_POLYGONS_DEFAULT_STYLE,
} from "./maplibre-polygons-layer.constants";
import {
  IMapLibrePolygonFeatureProperties,
  IMapLibrePolygonItem,
  IMapLibrePolygonsStyle,
} from "./maplibre-polygons-layer.models";

export const resolveMapLibrePolygonsStyle = (
  style?: Partial<IMapLibrePolygonsStyle>,
): IMapLibrePolygonsStyle => ({ ...MAPLIBRE_POLYGONS_DEFAULT_STYLE, ...style });

/**
 * One GeoJSON source for every polygon on the map, each feature carrying its own paint values.
 *
 * Leaflet gave each area its own `L.geoJSON` layer with its own style object; MapLibre would need one
 * source + two layers per area to do the same, which is per-layer draw-call overhead for nothing. Instead
 * the per-area values ride on the features and the paint reads them back with `["get", …]`, so any number
 * of areas costs two layers total.
 */
export const buildMapLibrePolygonsFeatureCollection = ({
  polygons,
  style,
  isInteractive,
}: {
  polygons: IMapLibrePolygonItem[];
  style: IMapLibrePolygonsStyle;
  isInteractive: boolean;
}): FeatureCollection => {
  const features = polygons.flatMap(
    ({ id, polygon, color, isDimmed, tooltip }) => {
      if (!polygon?.features?.length) {
        return [];
      }

      const properties: IMapLibrePolygonFeatureProperties = {
        polygonId: id,
        color,
        fillOpacity: isDimmed ? style.dimmedFillOpacity : style.fillOpacity,
        strokeOpacity: isDimmed
          ? style.dimmedStrokeOpacity
          : MAPLIBRE_POLYGON_STROKE_OPACITY,
        weight: isDimmed ? style.dimmedWeight : style.weight,
        // Dimmed polygons are background context only — they must not intercept clicks or hover, which is
        // what Leaflet's `interactive: false` did for them.
        isInteractive: isInteractive && !isDimmed,
        tooltip: tooltip ?? "",
      };

      return polygon.features.map(
        feature =>
          ({
            type: "Feature",
            properties,
            geometry: feature.geometry,
          }) as Feature<Polygon>,
      );
    },
  );

  return { type: "FeatureCollection", features };
};

/**
 * Every polygon vertex as a location, so polygon screens can reuse `useMapLibreLocationsAutoFit` for
 * framing instead of each one hand-rolling a `fitBounds`.
 */
export const collectPolygonLocations = (
  polygons: IMapLibrePolygonItem[],
): ILocationInfo[] =>
  collectPolygonPositions(polygons).map(([lng, lat]) => ({
    latitude: lat,
    longitude: lng,
  }));

/** Every `[lng, lat]` position in the collection, for bounds fitting. */
const collectPolygonPositions = (
  polygons: IMapLibrePolygonItem[],
): [number, number][] => {
  const positions: [number, number][] = [];

  polygons.forEach(({ polygon }) => {
    polygon?.features?.forEach(feature => {
      collectGeometryPositions(feature.geometry, positions);
    });
  });

  return positions;
};

/**
 * Rings nest to a different depth for Polygon (2) than MultiPolygon (3), and a bad row can carry
 * something else entirely — so walk the arrays rather than assuming a depth.
 */
const collectGeometryPositions = (
  geometry: IGeoJSON["features"][number]["geometry"] | undefined,
  positions: [number, number][],
): void => {
  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) {
      return;
    }

    const [first, second] = node;

    if (typeof first === "number" && typeof second === "number") {
      positions.push([first, second]);
      return;
    }

    node.forEach(walk);
  };

  walk((geometry as { coordinates?: unknown })?.coordinates);
};
